import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { NoShowIcon } from '@calendly/ui/components/icons';
import classNames from 'classnames';
import { useState, forwardRef } from 'react';
import { RemoteContactCard, RemoteContactsSearchHandler } from 'features/crm';
import { CONTACT_INTERACTION_LOCATIONS } from 'hooks/analytics_events/constants';
import { FullStoryExclude, FullStoryMask } from '../../../shared/fullstory_privacy';
import { getInitials } from '../../../shared/utils/get_initials';
import { LinkBadge } from '../linkBadge';

/**
 * ContactBadge fetches and displays a contact's information in a badge format.
 *
 * This component fetches contact data by ID and displays the contact's name
 * in a LinkBadge component with an avatar showing initials. Shows a loading
 * state while fetching and handles the "contact not found" case.
 *
 * @param {Object} props - Component props
 * @param {string} props.contactId - The ID of the contact to fetch
 * @param {boolean} [props.disabled] - Whether the badge is explicitly disabled
 * @param {Object} [props.analyticsHandlers] - Analytics handlers for tracking events
 *
 * @returns {JSX.Element} A badge component showing contact avatar and name
 *
 * @example
 * ```jsx
 * <ContactBadge
 *   contactId="717e2ba1-5102-40d3-9f13-45c2d2e58606"
 *   analyticsHandlers={analyticsHandlers}
 * />
 *
 * <ContactBadge
 *   contactId="invalid-id"
 *   disabled
 * />
 * ```
 */

const inlineContainerCss = "ifolweo";
const InlineContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(inlineContainerCss, props.className)} />;
});
export const ContactBadge = ({
  contactId,
  disabled,
  analyticsHandlers
}) => {
  const [contact, setContact] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const handleResults = results => {
    const matchedContact = results?.find(result => result.id === contactId);
    setContact(matchedContact);
  };
  const handleFetching = isFetching => {
    setIsLoading(isFetching);
  };
  const isContactNotFound = !isLoading && !contact && !disabled;
  const isDisabled = disabled || isContactNotFound;
  const displayName = contact?.name || I18n.t('publisher.agent_hub.contact_not_found');
  const initials = contact?.name ? getInitials(contact.name) : '';
  let leftElement = null;
  if (isDisabled) {
    leftElement = <NoShowIcon />;
  } else if (displayName) {
    leftElement = <FullStoryExclude>
        <Avatar letter={initials} size="x-small" alt={displayName} src={contact?.previewUrl} />
      </FullStoryExclude>;
  }
  const onClick = () => {
    if (contactId) {
      if (typeof analyticsHandlers?.trackContactInteraction === 'function') {
        analyticsHandlers.trackContactInteraction('full_page_view', contactId);
      }
      if (typeof analyticsHandlers?.trackNavigationLinkInChatClicked === 'function') {
        analyticsHandlers.trackNavigationLinkInChatClicked('contact_link');
      }
      window.open(`/app/contacts/${contactId}`, '_blank', 'noopener,noreferrer');
    }
  };
  const linkBadge = <LinkBadge text={displayName} leftElement={leftElement} onClick={onClick} disabled={isDisabled} />;
  return <>
      <RemoteContactsSearchHandler contactId={contactId} onResults={handleResults} onFetching={handleFetching} />
      {isLoading ? <InlineContainer>
          <LinkBadge isLoading />
        </InlineContainer> : <InlineContainer>
          {contact ? <FullStoryExclude>
              <RemoteContactCard contact={contact} position="below" hoverCardLocation={CONTACT_INTERACTION_LOCATIONS.ASK_CALENDLY_CHAT} fullProfileLocation={CONTACT_INTERACTION_LOCATIONS.CONTACT_HOVER_CARD_ASK_CALENDLY_CHAT}>
                {linkBadge}
              </RemoteContactCard>
            </FullStoryExclude> : <FullStoryMask>
              {linkBadge}
            </FullStoryMask>}
        </InlineContainer>}
    </>;
};

require("./ContactBadge.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./ContactBadge.js");